// Analytics library - KPI data fetching for frequentation by mission
// UPDATED: Analytics v3 - removed trend computation; added globalDailySeries to FrequentationData
//   for "Tous les cours" aggregated view in the single-chart layout
// UPDATED: Added MissionOption interface for shared use across analytics components and hooks
// UPDATED: Added FrequentationEntry types and getFrequentationEntries/getFrequentationEntriesCount
//   functions for paginated detail view (includes occurrences with NULL frequentation)
// UPDATED: Added scheduledTime to AnalyticsFilters and FrequentationEntry; getFrequentationData
//   now calls get_frequentation_kpi_data RPC to support time-slot filtering; added
//   getDistinctScheduledTimes function

import { supabase } from './supabase';

export interface AnalyticsFilters {
  dateFrom: string;
  dateTo: string;
  siteId?: string | null;
  scheduledTime?: string | null;
}

export interface MissionOption {
  templateId: string;
  templateName: string;
}

export interface MissionDayPoint {
  date: string;
  total: number;
  sessionCount: number;
}

export interface MissionSummary {
  templateId: string;
  templateName: string;
  total: number;
  sessionCount: number;
  avgPerSession: number;
  dailySeries: MissionDayPoint[];
}

export interface FrequentationData {
  kpiId: string;
  kpiName: string;
  globalTotal: number;
  globalSessionCount: number;
  globalAvgPerSession: number;
  globalDailySeries: MissionDayPoint[];
  missions: MissionSummary[];
}

export async function getFrequentationData(filters: AnalyticsFilters): Promise<FrequentationData[]> {
  const { data, error } = await supabase.rpc('get_frequentation_kpi_data', {
    p_date_from: filters.dateFrom,
    p_date_to: filters.dateTo,
    p_site_id: filters.siteId ?? null,
    p_scheduled_time: filters.scheduledTime ?? null
  });

  if (error) throw error;
  if (!data || (data as unknown[]).length === 0) return [];

  const kpiMap = new Map<string, {
    name: string;
    entries: Array<{ date: string; value: number; templateId: string | null; templateName: string | null }>;
  }>();

  for (const row of data as Array<{
    kpi_definition_id: string;
    kpi_name: string;
    occurrence_date: string;
    template_id: string | null;
    template_name: string | null;
    numeric_value: number;
  }>) {
    const kpiId = row.kpi_definition_id as string;
    const name = row.kpi_name ?? 'Indicateur';

    if (!kpiMap.has(kpiId)) {
      kpiMap.set(kpiId, { name, entries: [] });
    }
    kpiMap.get(kpiId)!.entries.push({
      date: row.occurrence_date as string,
      value: Number(row.numeric_value),
      templateId: row.template_id as string | null,
      templateName: row.template_name ?? null
    });
  }

  const result: FrequentationData[] = [];

  for (const [kpiId, { name: kpiName, entries }] of kpiMap) {
    const missionMap = new Map<string, {
      name: string;
      dayMap: Map<string, number[]>;
    }>();

    for (const e of entries) {
      if (!e.templateId) continue;
      const key = e.templateId;
      if (!missionMap.has(key)) {
        missionMap.set(key, { name: e.templateName ?? 'Mission', dayMap: new Map() });
      }
      const m = missionMap.get(key)!;
      if (!m.dayMap.has(e.date)) {
        m.dayMap.set(e.date, []);
      }
      m.dayMap.get(e.date)!.push(e.value);
    }

    const missions: MissionSummary[] = [];

    for (const [templateId, { name: templateName, dayMap }] of missionMap) {
      const dailySeries: MissionDayPoint[] = Array.from(dayMap.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, values]) => ({
          date,
          total: values.reduce((s, v) => s + v, 0),
          sessionCount: values.length
        }));

      const total = dailySeries.reduce((s, p) => s + p.total, 0);
      const sessionCount = dailySeries.reduce((s, p) => s + p.sessionCount, 0);
      const avgPerSession = sessionCount > 0
        ? Math.round((total / sessionCount) * 10) / 10
        : 0;

      missions.push({ templateId, templateName, total, sessionCount, avgPerSession, dailySeries });
    }

    missions.sort((a, b) => b.total - a.total);

    const globalDateMap = new Map<string, { total: number; sessionCount: number }>();
    for (const mission of missions) {
      for (const p of mission.dailySeries) {
        if (!globalDateMap.has(p.date)) {
          globalDateMap.set(p.date, { total: 0, sessionCount: 0 });
        }
        const existing = globalDateMap.get(p.date)!;
        existing.total += p.total;
        existing.sessionCount += p.sessionCount;
      }
    }
    const globalDailySeries: MissionDayPoint[] = Array.from(globalDateMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, { total, sessionCount }]) => ({ date, total, sessionCount }));

    const globalTotal = missions.reduce((s, m) => s + m.total, 0);
    const globalSessionCount = missions.reduce((s, m) => s + m.sessionCount, 0);
    const globalAvgPerSession = globalSessionCount > 0
      ? Math.round((globalTotal / globalSessionCount) * 10) / 10
      : 0;

    result.push({ kpiId, kpiName, globalTotal, globalSessionCount, globalAvgPerSession, globalDailySeries, missions });
  }

  return result.sort((a, b) => a.kpiName.localeCompare(b.kpiName));
}

export interface FrequentationEntry {
  occurrenceDate: string;
  occurrenceId: string;
  templateId: string;
  missionName: string;
  userName: string | null;
  numericValue: number | null;
  scheduledTime: string | null;
}

export interface FrequentationEntriesFilters extends AnalyticsFilters {
  templateId?: string | null;
}

export async function getFrequentationEntries(
  filters: FrequentationEntriesFilters,
  limit: number,
  offset: number
): Promise<FrequentationEntry[]> {
  const { data, error } = await supabase.rpc('get_frequentation_entries', {
    p_date_from: filters.dateFrom,
    p_date_to: filters.dateTo,
    p_site_id: filters.siteId ?? null,
    p_template_id: filters.templateId ?? null,
    p_scheduled_time: filters.scheduledTime ?? null,
    p_limit: limit,
    p_offset: offset
  });

  if (error) throw error;
  if (!data) return [];

  return (data as Array<{
    occurrence_date: string;
    occurrence_id: string;
    template_id: string;
    mission_name: string;
    user_name: string | null;
    numeric_value: number | null;
    scheduled_time: string | null;
  }>).map(row => ({
    occurrenceDate: row.occurrence_date,
    occurrenceId: row.occurrence_id,
    templateId: row.template_id,
    missionName: row.mission_name ?? '',
    userName: row.user_name,
    numericValue: row.numeric_value != null ? Number(row.numeric_value) : null,
    scheduledTime: row.scheduled_time ?? null
  }));
}

export async function getFrequentationEntriesCount(
  filters: FrequentationEntriesFilters
): Promise<number> {
  const { data, error } = await supabase.rpc('get_frequentation_entries_count', {
    p_date_from: filters.dateFrom,
    p_date_to: filters.dateTo,
    p_site_id: filters.siteId ?? null,
    p_template_id: filters.templateId ?? null,
    p_scheduled_time: filters.scheduledTime ?? null
  });

  if (error) throw error;
  return Number(data ?? 0);
}

export async function getDistinctScheduledTimes(
  filters: Pick<FrequentationEntriesFilters, 'dateFrom' | 'dateTo' | 'siteId' | 'templateId'>
): Promise<string[]> {
  const { data, error } = await supabase.rpc('get_frequentation_distinct_times', {
    p_date_from: filters.dateFrom,
    p_date_to: filters.dateTo,
    p_site_id: filters.siteId ?? null,
    p_template_id: filters.templateId ?? null
  });

  if (error) throw error;
  if (!data) return [];

  return (data as Array<{ scheduled_time: string }>).map(row => row.scheduled_time);
}
